import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Flag, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Button } from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import { REPORT_CONTENT_MUTATION } from '../../graphql/mutations';

const REPORT_CATEGORIES = [
  { value: 'SPAM', label: 'spam-or-misleading' },
  { value: 'HARASSMENT', label: 'harassment-or-bullying' },
  { value: 'HATE_SPEECH', label: 'hate-speech' },
  { value: 'NSFW', label: 'inappropriate-or-nsfw' },
  { value: 'COPYRIGHT', label: 'copyright-infringement' },
  { value: 'AI_GENERATED', label: 'ai-generated-content' },
  { value: 'IMPERSONATION', label: 'impersonation' },
  { value: 'OTHER', label: 'other' },
] as const;

interface ReportUserDialogProps {
  open: boolean;
  contentType?: string;
  contentId: string;
  title?: string;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export function ReportUserDialog({
  open,
  contentType = 'user',
  contentId,
  title,
  onClose,
  onSuccess,
  onError,
}: ReportUserDialogProps) {
  const { t } = useTranslation('community');
  const { t: tc } = useTranslation('common');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [reportContent, { loading }] = useMutation(REPORT_CONTENT_MUTATION, {
    onCompleted: () => {
      setIsSubmitted(true);
      setError(null);
      onSuccess?.();
    },
    onError: (err) => {
      setError(err.message);
      onError?.(err.message);
    },
  });

  function handleSubmit() {
    if (!category) return;
    setError(null);
    reportContent({
      variables: {
        contentType,
        contentId,
        category,
        description: description.trim() || undefined,
      },
    });
  }

  function handleClose() {
    if (loading) return;
    onClose();
    setTimeout(() => {
      setCategory('');
      setDescription('');
      setIsSubmitted(false);
      setError(null);
    }, 300);
  }

  const dialogTitle = title ?? t('report.title.content');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()}>
        {isSubmitted ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-900">{t('report.success.title')}</h2>
            <p className="text-sm text-gray-600 max-w-[280px]">{t('report.success.body')}</p>
            <Button onClick={handleClose} className="mt-1">
              {t('report.done')}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{dialogTitle}</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">{t('report.prompt')}</p>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>
              )}

              <div className="flex flex-col gap-1.5">
                {REPORT_CATEGORIES.map((cat) => {
                  const isSelected = category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      onClick={() => setCategory(cat.value)}
                      className={`flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-left transition-all border ${
                        isSelected
                          ? 'bg-gray-100 border-gray-900 ring-1 ring-gray-900'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? 'border-gray-900' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <div className="w-2 h-2 rounded-full bg-gray-900" />}
                      </div>
                      <span className="text-sm text-gray-900">{t(`report.reason.${cat.label}`)}</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-900">{t('report.details-label')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('report.details-placeholder')}
                  className="w-full h-20 px-3 py-2.5 text-sm rounded-lg border border-gray-200 resize-none focus:outline-hidden focus:ring-1 focus:ring-gray-900 focus:border-gray-900 placeholder:text-gray-400 transition-colors"
                  disabled={loading}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={loading}>
                {tc('cancel')}
              </Button>
              <Button onClick={handleSubmit} disabled={loading || !category} variant="destructive">
                <Flag className="size-4" />
                {loading ? t('report.submitting') : t('report.submit')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
